import { IconPositions } from "@/baseComponents";
import { Button } from "@/components/atoms/button";
import { MediaImage } from "@/components/atoms/media";
import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { ContactLinkTypes, MediaTypes } from "@/types/enums";
import { formatContactUrl } from "@/utils/contactUrlFormatter";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import {
  Box,
  chakra,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { RxMobile } from "react-icons/rx";

export interface HeaderContactDetails {
  firstName: string;
  lastName: string;
  jobTitle?: string;
  mediaData?: MediaImageData;
  email?: string;
  phone?: string;
}

export interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactTitle?: string;
  contactSubtitle?: string;
  contactDetails?: HeaderContactDetails;
}

export default chakra(ContactModal);
function ContactModal({
  isOpen,
  onClose,
  contactTitle,
  contactSubtitle,
  contactDetails,
}: ContactModalProps) {
  const t = useTranslations();
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalContent
        display={{ base: "block", lg: "none" }}
        position="fixed"
        bottom={0}
        minW="full"
        p={4}
        rounded="1rem"
        borderRadius="none"
        borderTopRadius={{ base: "1rem", md: "none" }}
        bgColor="brand.grey"
        boxShadow={{
          base: "0 -4px 34px 0 rgba(0, 0, 0, 0.15)",
          md: "none",
        }}
      >
        <Box position="sticky">
          <ModalHeader
            p={0}
            textStyle="display.xs"
            fontSize="1.75rem"
            fontWeight={400}
            color="brand.aubergine"
          >
            {contactTitle ?? t("funds Contact Title")}
          </ModalHeader>

          <Text mb={3} textStyle="labelmd" color="brand.cyan">
            {contactSubtitle ?? t("funds Contact SubTitle")}
          </Text>

          <ModalCloseButton
            position="absolute"
            top={-2}
            right={-2}
            size="md"
            color="brand.aubergine"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
          />

          <Divider borderColor="aubergine.200" />
        </Box>

        <ModalBody p={0}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={5}
            mt={8}
          >
            {contactDetails?.mediaData &&
              contactDetails.mediaData.type === MediaTypes.Image && (
                <Box w={40} h={40} minW={40} flexShrink={0}>
                  <MediaImage
                    w="full"
                    h="full"
                    rounded="full"
                    viewportWidthFactor={{ base: 0.5 }}
                    cropSizeAlias={{ base: "square" }}
                    imageUrlBuilder={buildImageUrl}
                    {...contactDetails.mediaData}
                  />
                </Box>
              )}
            <Text
              textAlign="center"
              textStyle="heading.md"
              fontWeight={300}
              color="aubergine.950"
            >
              {contactDetails?.firstName} {contactDetails?.lastName}
            </Text>

            <Text
              textAlign="center"
              textStyle="paragraph.lg"
              color="aubergine.950"
            >
              {contactDetails?.jobTitle}
            </Text>
          </Box>

          <Box display="flex" gap={2} w="full" mt={5}>
            {contactDetails?.email && (
              <Button
                text={t("funds Contact Message")}
                url={formatContactUrl(
                  ContactLinkTypes.Email,
                  contactDetails.email,
                )}
                icon={<MdOutlineAlternateEmail />}
                iconPosition={IconPositions.Left}
                buttonProps={{ flex: 1, justifyContent: "center" }}
              />
            )}

            {contactDetails?.phone && (
              <Button
                text={t("funds Contact Call")}
                url={formatContactUrl(
                  ContactLinkTypes.Phone,
                  contactDetails.phone,
                )}
                icon={<RxMobile />}
                iconPosition={IconPositions.Left}
                buttonProps={{ flex: 1, justifyContent: "center" }}
              />
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
